import { Form, Formik } from "formik";
import {
  BACK_BUTTON_TEXT,
  CURRENT_HEALTH_INSURANCE,
  LOADING_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
} from "pages/CensusAndCurrentInformation/utils/constants";
import React, { useEffect, useState } from "react";
import { CurrentInsuranceType } from "types";
import FooterButton from "uicomponents/FooterButton";
import {
  trackFormError,
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import CurrentInsuranceComponent from "./components/CurrentInsuranceComponent";
import styles from "./CurrentInsurance.module.css";
import { currentInsuranceFormValues } from "pages/CensusAndCurrentInformation/utils/initialFormValues";
import { currentInsuranceValidationSchema } from "pages/CensusAndCurrentInformation/utils/validationSchema";
import {
  useUpdateCurrentInsuranceMutation,
  useUpdateProspectInfoMutation,
} from "types/generated/operations";
import Alert from "uicomponents/Alert";
import {
  convertStringIntoBool,
  ConvertToISODate,
  errorMessageMap,
  hasQueryParam,
} from "utils/helper";
import { getProspectUuid } from "utils/idHelpers";
import { CurrentInsuranceFormType } from "pages/CensusAndCurrentInformation/utils/formTypes";

interface ICurrentInsuranceProps {
  data: CurrentInsuranceType;
  setNextStep: (submitApp?: boolean) => void;
  setPreviousStep: () => void;
}

const CurrentInsurance = (props: ICurrentInsuranceProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(4, "Current health insurance", 1);
  }, []);

  const { data, setNextStep, setPreviousStep } = props;

  const hasCobra = data?.healthInsuranceInformation?.cobraUsage;
  const nextButtonText = hasCobra ? NEXT_BUTTON_TEXT : "Save & submit";

  const initialFormValues = currentInsuranceFormValues(data);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const hi_flow_with_core_app = hasQueryParam("usecoreapp");

  const [updateCurrentInsurance, { loading: updateCurrentInsuranceLoading }] =
    useUpdateCurrentInsuranceMutation();
  const [updateProspectInfo, { loading: updateProspectInfoLoading }] =
    useUpdateProspectInfoMutation();

  const handleSubmit = async (values: CurrentInsuranceFormType) => {
    try {
      setIsLoadingSteps(true);
      await updateCurrentInsurance({
        variables: {
          uuid: getProspectUuid(),
          controlledGroup: convertStringIntoBool(values.controlledGroup),
          startingCoverageDate: values.startingCoverageDate
            ? ConvertToISODate(values.startingCoverageDate)
            : undefined,
          isOnPeo: convertStringIntoBool(values.isOnPeo),
          currentPeo: values.currentPeo,
          currentPeoOther: values.currentPeoOther,
        },
      });

      await updateProspectInfo({
        variables: {
          uuid: getProspectUuid(),
          sicCode: values.sicCode,
          yearFounded: Number(values.yearFounded),
          companyName: data?.companyName,
          dba: data?.dba,
        },
      });
      trackSaveAndContinueUserStep(4, "Current health insurance", 1);

      if (!hasCobra) {
        setNextStep(true);
      } else {
        setNextStep();
      }
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  return (
    <div>
      <div>
        {hi_flow_with_core_app ? null : (
          <div className={styles.title}>{CURRENT_HEALTH_INSURANCE.TITLE}</div>
        )}
        <Formik
          initialValues={initialFormValues}
          validationSchema={currentInsuranceValidationSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form>
              <CurrentInsuranceComponent data={data} />
              {isError && <Alert message={errorMessage} />}
              <FooterButton
                nextButtonText={
                  updateCurrentInsuranceLoading ||
                  updateProspectInfoLoading ||
                  isLoadingSteps
                    ? LOADING_BUTTON_TEXT
                    : nextButtonText
                }
                backButtonText={BACK_BUTTON_TEXT}
                disabled={
                  updateCurrentInsuranceLoading ||
                  updateProspectInfoLoading ||
                  isLoadingSteps
                }
                handleBackClick={setPreviousStep}
              />
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CurrentInsurance;
